/**
 * Наблюдения: поводы, которые приложение замечает само (§14 плана).
 *
 * Это вторая половина советника. Первая — разговор по вопросу человека — уже
 * есть; здесь приложение само замечает, что случилось что-то, требующее
 * решения, и говорит об этом. Раньше человек узнавал о дефиците, только если
 * сам открывал главную и складывал числа глазами.
 *
 * Три правила, которые здесь заданы кодом, а не дисциплиной.
 *
 * · **Кандидаты считаются без модели.** Каждое наблюдение — арифметика по
 *   фактам, которую можно повторить руками. Модель ничего не «замечает»: она
 *   объясняет, и только когда человек сам об этом попросил. Автоматический
 *   вызов модели стоит денег и в правилах продукта не разрешён.
 * · **Причина не утверждается без расчёта.** «Из-за трёх покупок цель
 *   сдвинулась» — утверждение, которого расчёт не поддерживает. Допустимая
 *   форма: «при нынешнем взносе цель ожидается в июле».
 * · **Повтор — только при изменении подписи.** Отложенное «не сейчас»
 *   действует, пока не изменилась суть: сумма, срок или статус. Иначе
 *   приложение превращается в напоминалку, которую перестают читать.
 *
 * Молчание — нормальное состояние. Если поводов нет, не показывается ничего.
 */
import { ZERO, add, formatMoney, sub, type Money } from './money.js';
import { diffDays, isAfter, type PlainDate } from './date.js';
import { goalEta, monthlyForDeadline } from './goals.js';
import type { AvailableResult } from './available.js';
import type { Goal } from './types.js';

export type InsightKind =
  /** «Можно потратить» ушло в минус. */
  | 'deficit'
  /** Наступление платежа, которому не хватает денег. */
  | 'payment_at_risk'
  /** По карте не собрана сумма для беспроцентного периода. */
  | 'grace_at_risk'
  /** Деньги пришли, а ближайшие платежи обеспечены не полностью. */
  | 'income_unallocated'
  /** Цель не успевает к названному сроку при нынешнем взносе. */
  | 'goal_behind';

export type InsightSeverity = 'info' | 'attention' | 'urgent';

export interface InsightCandidate {
  kind: InsightKind;
  /** Устойчивый ключ: по нему живёт отметка «не сейчас». */
  key: string;
  severity: InsightSeverity;
  /** Числа, на которых стоит наблюдение. Показываются человеку. */
  facts: Record<string, Money | number | string>;
  /** Что случилось — одной фразой, без причинных утверждений. */
  text: string;
  /** Что можно сделать. `null` — повод есть, а действия нет. */
  action: { label: string; target: string } | null;
  /**
   * Подпись сути. Меняется — наблюдение показывается снова, даже если было
   * отложено: это уже другой факт, а не тот же самый.
   */
  signature: string;
}

/** Что человек уже видел и что отложил. */
export interface InsightDisplayState {
  key: string;
  shownAt: string;
  dismissedAt?: string;
  signature: string;
}

export interface InsightInput {
  today: PlainDate;
  available: AvailableResult;
  goals: readonly Goal[];
  /** Приходы, записанные фактом: дата и сумма. */
  incomes: readonly { date: PlainDate; amount: Money }[];
  /** Сколько дней считать приход свежим. */
  freshDays?: number;
}

const MONTHS = [
  'января', 'февраля', 'марта', 'апреля', 'мая', 'июня',
  'июля', 'августа', 'сентября', 'октября', 'ноября', 'декабря',
];

/**
 * Дата словами: «21 сентября».
 *
 * Наблюдение читают на бегу, а «2026-09-21» на бегу не читается вовсе. Год
 * опускается: все поводы живут в пределах ближайших недель.
 */
const day = (d: PlainDate): string => {
  const [, m, dd] = d.split('-');
  return `${Number(dd)} ${MONTHS[Number(m) - 1]}`;
};
/** С годом: у цели срок бывает через год, и «13 марта» против «15 марта» читалось бы как успеваю. */
const dayYear = (d: PlainDate): string => `${day(d)} ${d.slice(0, 4)}`;

/** Порог свежести прихода: после него «пришли деньги» уже не новость. */
const FRESH_DAYS = 3;

/** На главной не больше двух: третье наблюдение читать уже никто не будет. */
export const MAX_SHOWN = 2;

const RANK: Record<InsightSeverity, number> = { urgent: 0, attention: 1, info: 2 };

/**
 * Поводы из сегодняшней картины.
 *
 * Порядок — по срочности, потом по близости срока. Список не обрезается:
 * сколько показать, решает экран, а журнал должен видеть всё.
 */
export function computeInsightCandidates(input: InsightInput): InsightCandidate[] {
  const { today, available } = input;
  const out: InsightCandidate[] = [];

  // 1. Дефицит. Самое дорогое, что может случиться с главной цифрой.
  if (available.available < ZERO) {
    const gap = sub(ZERO, available.available) as Money;
    out.push({
      kind: 'deficit',
      key: 'deficit',
      severity: 'urgent',
      facts: { gap, daysToHorizon: available.daysToHorizon },
      text: `Не хватает ${formatMoney(gap)} до ближайшего дохода`,
      action: { label: 'Спросить SKOKA', target: 'ask:Не хватает денег до зарплаты. Что сделать?' },
      signature: `deficit:${gap}`,
    });
  }

  /*
    2. Платежи, до которых денег не хватит.

    Два фильтра, без которых наблюдения превращаются в шум.

    · **Нехватка считается деньгами, а не конвертами.** Человек с 70 000 ₽ на
      счёте получал тревогу о мобильной связи за 650 ₽ только потому, что под
      неё не создан конверт. Это не нехватка, а неоформленность.
    · **Мелочь не повод.** Платёж меньше дневного темпа трат не стоит
      внимания: он растворится в обычной жизни без всякого решения.

    Срок тоже должен быть виден: тревожить за месяц бессмысленно, за день —
    поздно.
  */
  for (const u of available.upcoming) {
    if (u.settled || u.remaining <= ZERO) continue;
    if (u.remaining <= available.perDay) continue;
    if (u.remaining <= available.available) continue;
    const days = diffDays(today, u.date);
    if (days > 10) continue;
    out.push({
      kind: 'payment_at_risk',
      key: `payment:${u.kind}:${u.id}:${u.date}`,
      severity: days <= 3 ? 'urgent' : 'attention',
      facts: { amount: u.amount, remaining: u.remaining, date: u.date, days },
      text: days < 0
        ? `${u.label}: срок был ${day(u.date)}, не хватает ${formatMoney(u.remaining)}`
        : `${u.label} ${day(u.date)}: не хватает ${formatMoney(u.remaining)}`,
      // «Что делать» — лист нехватки этого платежа: сколько, к какому сроку и варианты (02.11.2026).
      action: { label: 'Что делать', target: 'payment' },
      // Подпись включает недобор: он изменился — повод новый.
      signature: `payment:${u.date}:${u.remaining}`,
    });
  }

  // 3. Беспроцентный период карты. Отдельно от платежей: цена ошибки здесь
  //    не «просрочка», а проценты на весь долг.
  for (const c of available.credit) {
    if (c.graceSecured || c.missing <= ZERO) continue;
    out.push({
      kind: 'grace_at_risk',
      key: `grace:${c.accountId}:${c.paymentDate}`,
      severity: c.daysToPayment <= 5 ? 'urgent' : 'attention',
      facts: {
        missing: c.missing, needForGrace: c.needForGrace,
        date: c.paymentDate, days: c.daysToPayment,
      },
      text: `${c.name}: до ${day(c.paymentDate)} не хватает ${formatMoney(c.missing)} `
        + 'для беспроцентного периода',
      action: { label: 'Отложить на платёж', target: `card:${c.accountId}` },
      signature: `grace:${c.paymentDate}:${c.missing}`,
    });
  }

  // 4. Деньги пришли, а платежи обеспечены не полностью.
  //
  //    Два проверяемых факта рядом, без утверждения о причине: приход был, и
  //    недобор есть. Что с этим делать, решает человек.
  const fresh = input.incomes.filter(
    (i) => !isAfter(i.date, today) && diffDays(i.date, today) <= (input.freshDays ?? FRESH_DAYS),
  );
  // Платежи после ближайшего прихода оплачивает он сам — их недобор
  // сегодняшним деньгам не упрёк. Прежде они входили в сумму, и на главной
  // рядом стояли «платежи до прихода обеспечены» и «не хватает 74 353 ₽»:
  // вторая цифра была кредиткой 9 октября, которую платит зарплата 7-го.
  const unsecured = add(...available.upcoming
    .filter((u) => !u.settled && !u.afterHorizon)
    .map((u) => u.remaining));
  if (fresh.length > 0 && unsecured > ZERO) {
    const got = add(...fresh.map((i) => i.amount));
    const last = fresh.map((i) => i.date).sort().at(-1)!;
    out.push({
      kind: 'income_unallocated',
      key: `income:${last}`,
      severity: 'attention',
      facts: { got, unsecured, date: last },
      text: `Пришло ${formatMoney(got)}, а ближайшим платежам не хватает ${formatMoney(unsecured)}`,
      action: { label: 'Разложить приход', target: 'distribute' },
      signature: `income:${last}:${got}:${unsecured}`,
    });
  }

  // 5. Цель не успевает к сроку.
  //
  //    Простыми словами и с годами: сколько откладывается, к какому дню
  //    соберётся и сколько нужно, чтобы успеть. Ни одного слова о причинах.
  for (const g of input.goals) {
    if (g.status !== 'active' || !g.targetDate) continue;
    const eta = goalEta(g, g.monthlyContribution, today);
    if (eta === null) {
      out.push({
        kind: 'goal_behind',
        key: `goal:${g.id}`,
        severity: 'attention',
        facts: { target: g.targetAmount, current: g.currentAmount, date: g.targetDate },
        text: isAfter(today, g.targetDate)
          ? `${g.name}: срок ${dayYear(g.targetDate)} уже прошёл — поставь новый`
          : `${g.name}: не указано, сколько откладывать в месяц — к ${dayYear(g.targetDate)} не собрать. `
            + `Чтобы успеть, нужно ≈ ${formatMoney(monthlyForDeadline(g, g.targetDate, today))} в месяц`,
        action: { label: 'Изменить цель', target: `goal:${g.id}` },
        signature: `goal:${g.id}:none:${g.targetDate}`,
      });
      continue;
    }
    if (!isAfter(eta, g.targetDate)) continue;
    out.push({
      kind: 'goal_behind',
      key: `goal:${g.id}`,
      severity: 'attention',
      facts: { eta, date: g.targetDate, monthly: g.monthlyContribution },
      text: isAfter(today, g.targetDate)
        ? `${g.name}: срок ${dayYear(g.targetDate)} уже прошёл — поставь новый`
        : `${g.name}: по ${formatMoney(g.monthlyContribution)} в месяц к ${dayYear(g.targetDate)} не успеть — `
          + `соберётся только к ${dayYear(eta)}. Чтобы успеть, нужно ≈ ${formatMoney(monthlyForDeadline(g, g.targetDate, today))} в месяц`,
      action: { label: 'Изменить цель', target: `goal:${g.id}` },
      signature: `goal:${g.id}:${eta}:${g.monthlyContribution}`,
    });
  }

  return out.sort((a, b) => RANK[a.severity] - RANK[b.severity]
    || String(a.facts.date ?? '').localeCompare(String(b.facts.date ?? '')));
}

/**
 * Что показать сейчас.
 *
 * Отложенное не возвращается, пока не изменилась подпись: сумма, срок или
 * статус. Это и есть разница между наблюдением и напоминалкой — второе
 * перестают читать через неделю.
 */
export function visibleInsights(
  candidates: readonly InsightCandidate[],
  journal: readonly InsightDisplayState[],
  limit = MAX_SHOWN,
): InsightCandidate[] {
  const seen = new Map(journal.map((j) => [j.key, j]));
  return candidates
    .filter((c) => {
      const before = seen.get(c.key);
      if (!before?.dismissedAt) return true;
      return before.signature !== c.signature;
    })
    .slice(0, limit);
}

/**
 * Записи журнала, которым больше нечего описывать.
 *
 * Повод исчез — платёж оплачен, дефицит закрыт, цель поправлена. Держать
 * отметку «отложено» дальше незачем: если то же случится снова, это будет
 * новый повод, а не продолжение старого.
 */
export const staleEntries = (
  candidates: readonly InsightCandidate[],
  journal: readonly InsightDisplayState[],
): InsightDisplayState[] => {
  const live = new Set(candidates.map((c) => c.key));
  return journal.filter((j) => !live.has(j.key));
};
